import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type DasaThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'dasa-theme';

/**
 * DASA TECH theme switcher. Writes `data-theme` on <html> so global CSS
 * variables in styles/_brand-tokens.scss flip across the whole app.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly mode$ = new BehaviorSubject<DasaThemeMode>(this.readInitialMode());
  readonly modeChanges = this.mode$.asObservable();

  constructor() {
    this.applyMode(this.mode$.value);
  }

  get current(): DasaThemeMode {
    return this.mode$.value;
  }

  set(mode: DasaThemeMode): void {
    if (this.mode$.value === mode) { return; }
    this.applyMode(mode);
    this.mode$.next(mode);
    try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* storage disabled */ }
  }

  toggle(): void {
    this.set(this.mode$.value === 'dark' ? 'light' : 'dark');
  }

  private applyMode(mode: DasaThemeMode): void {
    if (typeof document === 'undefined') { return; }
    document.documentElement.setAttribute('data-theme', mode);
  }

  private readInitialMode(): DasaThemeMode {
    if (typeof document === 'undefined') { return 'light'; }
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'dark' || saved === 'light') { return saved; }
    } catch { /* storage disabled */ }
    // Respect document attribute set in index.html, else default to light.
    const attr = document.documentElement.getAttribute('data-theme');
    return attr === 'dark' ? 'dark' : 'light';
  }
}
